// Recorridos guiados: la version corta de cada pantalla.
//
// Un recorrido explica para que sirve una pantalla en un minuto. Lo que hace
// cada campo, cada estado y cada mensaje de error va en el manual — el paso
// enlaza ahi con `help`, y no repite el texto.
//
// Los pasos apuntan al MISMO atributo que usa el modo señalar (`data-help`).
// Una sola marca en el codigo sostiene las dos cosas: si un elemento se
// renombra, el recorrido y el enlace de ayuda se rompen juntos y el script de
// verificacion los levanta a los dos.

import type { HelpId } from '@/help/targets';

export interface TourStep {
  /** Selector del elemento a resaltar. Si no esta en pantalla, el paso se saltea. */
  target: string;
  title: string;
  body: string;
  /** Lado preferido para la tarjeta. Si no entra, se elige otro. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Punto del manual con la explicacion completa. */
  help?: HelpId;
}

export interface Tour {
  id: string;
  title: string;
  /** Rutas del portal en las que se ofrece este recorrido. */
  match: RegExp;
  steps: readonly TourStep[];
}

/** Azucar: los recorridos apuntan a elementos por su id de ayuda. */
const at = (id: HelpId): string => `[data-help="${id}"]`;

export const TOURS: readonly Tour[] = [
  {
    id: 'bienvenida',
    title: 'Cómo moverse',
    match: /^\/dashboard\/?$/,
    steps: [
      {
        target: at('shell.nav'),
        title: 'El menú lateral',
        body: 'Todo el backoffice cuelga de acá. Cada entrada es una sección independiente; lo que no ves es porque tu cuenta no tiene permiso para esa parte.',
        placement: 'right',
        help: 'shell.nav',
      },
      {
        target: at('dashboard.metrics'),
        title: 'Los números de arriba',
        body: 'Un resumen del estado del sistema en este momento: cuentas, suscripciones y actividad. Sirven para detectar que algo se salió de lo normal, no para contabilidad.',
        help: 'dashboard.metrics',
      },
      {
        target: at('shell.help'),
        title: 'La ayuda, siempre a mano',
        body: 'Desde acá abrís el manual, arrancás el recorrido de la pantalla en la que estés, o activás el modo señalar para preguntar por un botón concreto.',
        placement: 'bottom',
        help: 'shell.help',
      },
      {
        target: at('shell.user-menu'),
        title: 'Tu cuenta',
        body: 'Tu nombre, tu rol y la salida. Desde “Mi cuenta” cambiás tus datos y tu contraseña.',
        placement: 'bottom',
        help: 'shell.user-menu',
      },
    ],
  },
  {
    id: 'usuarios',
    title: 'Buscar una cuenta',
    match: /^\/users\/?$/,
    steps: [
      {
        target: at('users.search'),
        title: 'Un solo campo',
        body: 'Pegá el email, el documento o el identificador de la cuenta. El sistema deduce qué le pasaste; no hace falta elegir nada.',
        help: 'users.search',
      },
      {
        target: at('users.search-name'),
        title: 'Por nombre',
        body: 'Cuando solo tenés el apellido. Coincide por el comienzo, sin tildes ni mayúsculas; agregá el nombre para achicar la lista.',
        help: 'users.search-name',
      },
      {
        target: at('users.results'),
        title: 'Los resultados',
        body: 'Con email, documento o identificador la coincidencia es exacta: si hay una sola cuenta, se abre directamente. Por nombre se listan todas las que empiezan igual, en orden alfabético.',
        help: 'users.results',
      },
      {
        target: at('users.pagination'),
        title: 'Pasar de página',
        body: 'La lista viene de a tandas. El rango muestra de qué apellido a cuál llega esta página; si hay más, agregar el nombre suele evitar recorrerlas.',
        help: 'users.pagination',
      },
    ],
  },
  {
    id: 'ficha-usuario',
    title: 'La ficha de una cuenta',
    match: /^\/users\/[^/]+\/?$/,
    steps: [
      {
        target: at('user.identity'),
        title: 'Quién es',
        body: 'Datos de identidad y estado de la cuenta. Los datos personales se muestran enmascarados salvo que los despliegues, y cada vez que se ven queda registrado.',
        help: 'user.identity',
      },
      {
        target: at('user.subscription'),
        title: 'Qué contrató',
        body: 'Plan vigente, estado y hasta cuándo tiene acceso. Es lo primero que hay que mirar cuando alguien dice que no puede ver el contenido.',
        help: 'user.subscription',
      },
      {
        target: at('user.devices'),
        title: 'Desde dónde entra',
        body: 'Los dispositivos con sesión abierta. Desde acá se puede cerrar uno solo, sin tocar los demás.',
        help: 'user.devices',
      },
      {
        target: at('user.actions'),
        title: 'Qué se puede hacer',
        body: 'Las acciones sobre la cuenta. Todas piden confirmación y todas quedan asentadas en el registro de auditoría con tu nombre.',
        help: 'user.actions',
      },
    ],
  },
  {
    id: 'partners',
    title: 'Los partners',
    match: /^\/partners\/?$/,
    steps: [
      {
        target: at('partners.page'),
        title: 'Cada plataforma es un partner',
        body: 'Un partner es una plataforma conectada al sistema. Tiene sus propios planes, su propia marca y sus propias aplicaciones.',
        help: 'partners.page',
      },
      {
        target: at('partners.new'),
        title: 'Alta de un partner',
        body: 'El alta deja todo listo para funcionar, incluidas las credenciales. No hace falta ningún paso fuera de esta pantalla.',
        placement: 'bottom',
        help: 'partners.new',
      },
    ],
  },
  {
    id: 'ficha-partner',
    title: 'Configurar un partner',
    match: /^\/partners\/[^/]+\/edit\/?$/,
    steps: [
      {
        target: at('partner.tabs'),
        title: 'Todo en solapas',
        body: 'Cada solapa es un aspecto distinto del partner. Los cambios se guardan por solapa: lo que edites en una no se pierde al pasar a otra, pero tampoco se guarda solo.',
        help: 'partner.tabs',
      },
      {
        target: at('partner.general.ttl'),
        title: 'Cuánto dura una sesión',
        body: 'Definen cuánto vale una sesión antes de tener que renovarse y cuánto tiempo se recuerda quién puede ver qué. Cambiarlos afecta a los usuarios reales de esa plataforma.',
        help: 'partner.general.ttl',
      },
      {
        target: at('partner.credentials'),
        title: 'Las credenciales',
        body: 'Las claves con las que la plataforma se identifica. Se generan solas al crear el partner; esta sección es para verlas, rotarlas o reparar una que quedó a medias.',
        help: 'partner.credentials',
      },
    ],
  },
  {
    id: 'facturas',
    title: 'Facturas',
    match: /^\/invoices\/?$/,
    steps: [
      {
        target: at('invoices.filters'),
        title: 'Encontrar un comprobante',
        body: 'Se filtra por estado, por partner y por fecha. El estado dice si el comprobante ya se emitió, si está esperando, o si falló.',
        help: 'invoices.filters',
      },
      {
        target: at('invoices.page'),
        title: 'Cuando algo falla',
        body: 'Un comprobante con error se puede reintentar desde acá. Si se emitió bien pero no llegó el correo, se reenvía sin volver a emitir nada.',
        help: 'invoices.retry',
      },
    ],
  },
  {
    id: 'trabajos',
    title: 'Procesos y colas',
    match: /^\/jobs\/?$/,
    steps: [
      {
        target: at('jobs.queues'),
        title: 'El estado de las colas',
        body: 'Cada cola mueve un tipo de trabajo. Lo que importa es la columna de la cola de descarte: si tiene mensajes, algo no se pudo entregar.',
        help: 'jobs.queues',
      },
      {
        target: at('jobs.redrive'),
        title: 'Reprocesar lo que falló',
        body: 'Devuelve los mensajes descartados a la cola normal para que se vuelvan a intentar. Usalo recién después de haber corregido la causa.',
        help: 'jobs.redrive',
      },
    ],
  },
  {
    id: 'auditoria',
    title: 'El registro de auditoría',
    match: /^\/audit\/?$/,
    steps: [
      {
        target: at('audit.filters'),
        title: 'Qué pasó y quién lo hizo',
        body: 'Cada acción con consecuencias queda registrada acá con su autor, su momento y su resultado. El registro no se puede editar ni borrar.',
        help: 'audit.page',
      },
    ],
  },
  {
    id: 'equipo',
    title: 'Cuentas del equipo',
    match: /^\/team\/?$/,
    steps: [
      {
        target: at('team.new'),
        title: 'Dar de alta a alguien',
        body: 'Se crea la cuenta con un rol y, si hace falta, permisos extra. La persona recibe una contraseña inicial que tiene que cambiar.',
        placement: 'bottom',
        help: 'team.new',
      },
      {
        target: at('team.permissions'),
        title: 'Rol y permisos',
        body: 'El rol define el alcance general. Los permisos extra habilitan zonas puntuales, y el rol más alto NO los incluye por defecto: se dan uno por uno.',
        help: 'team.permissions',
      },
    ],
  },
] as const;

/** El recorrido que corresponde a una ruta del portal, si hay alguno. */
export function tourForPath(path: string): Tour | null {
  return TOURS.find((t) => t.match.test(path)) ?? null;
}

export function tourById(id: string): Tour | null {
  return TOURS.find((t) => t.id === id) ?? null;
}
